/**
 * Settings > Security > Single sign-on (CC 0173). Route: settings.sso (settings.read
 * to view; editing needs security.sso.manage, proposed).
 *
 * A tenant admin sets up staff sign-in without asking us: Microsoft sign-in domains
 * (verified against the directory connected on Integrations > Microsoft), one SAML 2.0
 * connection, the "only Microsoft-synced staff" rule, the default role for first
 * sign-ins, and the last 20 sign-in attempts. Every value comes from the server through
 * ./sso-api (proposed operations); nothing on this screen is seeded locally.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Btn, Card, Dropdown, Icon, Input, Pill, T, Toggle, tx } from "@v0y4ge/jrni-ui";
import { PageHeader } from "../../shell/chrome";
import SettingsShell from "./SettingsShell";
import { can } from "../../shell/access";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import {
  getSsoSettings, updateMicrosoftSignIn, addMicrosoftSignInDomain, removeMicrosoftSignInDomain,
  saveSamlConnection, deleteSamlConnection, listSsoSignInActivity, isMicrosoftIssuer, isDomain,
  SSO_FIELD_TEXT, reasonText, domainReasonText, ssoErrorText, blockerText,
  type SsoSettings, type SsoActivityRow, type SsoRole, type SsoSaml,
} from "./sso-api";

export const SSO_MANAGE_CAP = "security.sso.manage";
const IconAny = Icon as unknown as Record<string, (p?: { size?: number }) => ReactNode>;
const ic = (name: string, size = 15) => { const C = IconAny[name] || IconAny.info; return <C size={size} />; };
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const DAY = 86_400_000;

const label: CSSProperties = { fontSize: 12, fontWeight: 600, color: T.n1, display: "block", marginBottom: 6 };
const help: CSSProperties = { fontSize: 11.5, color: T.soft, lineHeight: 1.5, margin: "6px 0 0" };
const errStyle: CSSProperties = { fontSize: 12, color: T.dangerSurfaceInk, margin: "6px 0 0", lineHeight: 1.5 };
/* The Integrations › Microsoft card language (CC 0222): a 12/16 header with h3 + help,
   then sections split by a hairline, each 14/16. */
const sectionHead: CSSProperties = { padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12 };
const section: CSSProperties = { padding: "14px 16px", borderBottom: `1px solid ${T.border}`, display: "grid", gap: 10 };
const sectionLast: CSSProperties = { ...section, borderBottom: "none" };
const sectionTitle: CSSProperties = { fontSize: 13, fontWeight: 600, color: T.n1, margin: 0 };
const linkBtn: CSSProperties = { border: "none", background: "transparent", padding: 0, color: T.p1, fontSize: 12.5, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 };

function fmtDate(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(+d) ? iso : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
function fmtWhen(iso: string) {
  const d = new Date(iso); if (isNaN(+d)) return iso;
  const mins = Math.round((Date.now() - +d) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

function Head({ icon, title, sub, right }: { icon: ReactNode; title: string; sub: string; right?: ReactNode }) {
  return (
    <div style={sectionHead}>
      <span style={{ width: 32, height: 32, borderRadius: 8, background: T.p3, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{title}</h3>
        <p style={{ ...tx.help, marginTop: 2, textWrap: "pretty" } as CSSProperties}>{sub}</p>
      </div>
      {right}
    </div>
  );
}

/** A numbered setup step: a filled number, a title and one line of what to do. */
function Step({ n, title, sub, right, children }: { n: number; title: string; sub?: string; right?: ReactNode; children: ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "28px minmax(0, 1fr)", columnGap: 12 }}>
      <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: 999, background: T.p1, color: "#fff", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>{n}</span>
      <div style={{ display: "grid", gap: 12, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 240px", minWidth: 0 }}>
            <div style={sectionTitle}>Step {n}. {title}</div>
            {sub ? <p style={{ ...tx.help, marginTop: 2 }}>{sub}</p> : null}
          </div>
          {right}
        </div>
        {children}
      </div>
    </div>
  );
}

/** Reads a SAML 2.0 Federation Metadata XML file in the browser (CC 0222): the IdP's
 *  entity ID, its sign-on URL (redirect binding first) and its signing certificate. */
function parseIdpMetadata(xml: string): { entityId: string; loginUrl: string; certificate: string } | null {
  let doc: Document;
  try { doc = new DOMParser().parseFromString(xml, "application/xml"); } catch { return null; }
  if (doc.getElementsByTagName("parsererror").length) return null;
  const all = (name: string, root: Document | Element = doc) => Array.from(root.getElementsByTagNameNS("*", name));
  const ed = all("EntityDescriptor")[0];
  const idp = all("IDPSSODescriptor")[0];
  if (!ed || !idp) return null;
  const sso = all("SingleSignOnService", idp);
  const pick = sso.find((x) => /HTTP-Redirect$/.test(x.getAttribute("Binding") || "")) ?? sso.find((x) => /HTTP-POST$/.test(x.getAttribute("Binding") || "")) ?? sso[0];
  const keys = all("KeyDescriptor", idp);
  const signing = keys.find((k) => (k.getAttribute("use") || "signing") === "signing") ?? keys[0];
  const cert = signing ? (all("X509Certificate", signing)[0]?.textContent || "").replace(/\s+/g, "") : "";
  return { entityId: ed.getAttribute("entityID") || "", loginUrl: pick?.getAttribute("Location") || "", certificate: cert };
}

/* The role picker's accessible name: the same words as its visible label (this file is not yet translated). */
const ROLE_SELECT_LABEL = "Role for people signing in for the first time";

function RoleSelect({ roles, value, onChange, disabled, id }: { roles: SsoRole[]; value: string | null; onChange: (v: string | null) => void; disabled?: boolean; id: string }) {
  return (
    <Dropdown<string> portal testid={id} aria-label={ROLE_SELECT_LABEL} value={value ?? ""} disabled={disabled} onChange={(v) => onChange(v || null)}
      style={{ width: "100%", maxWidth: 320 }}
      options={[{ value: "", label: "Choose a role" }, ...roles.map((r) => ({ value: r.key, label: r.label }))]} />
  );
}

function SwitchRow({ on, onChange, title, sub, disabled, testid }: { on: boolean; onChange?: () => void; title: string; sub?: ReactNode; disabled?: boolean; testid?: string }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", opacity: disabled ? 0.6 : 1 }} data-testid={testid}>
      <Toggle on={on} onChange={disabled ? undefined : onChange} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: T.n1 }}>{title}</div>
        {sub ? <div style={help}>{sub}</div> : null}
      </div>
    </div>
  );
}

function CopyRow({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(110px, 160px) minmax(0, 1fr) auto", gap: 12, alignItems: "center", padding: "10px 0", borderTop: `1px solid ${T.border}` }}>
      <span style={{ fontSize: 12, color: T.soft }}>{k}</span>
      <code style={{ fontSize: 12, color: T.n1, overflowWrap: "anywhere", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{v || "—"}</code>
      <Btn variant="ghost" size="sm" disabled={!v} onClick={() => { void navigator.clipboard?.writeText(v).then(() => toast({ tone: "success", title: `${k} copied` })); }}>Copy</Btn>
    </div>
  );
}

function domainPill(status: string) {
  if (status === "verified") return <Pill tone="success" dot>Verified</Pill>;
  if (status === "refused") return <Pill tone="danger">Not verified</Pill>;
  return <Pill tone="warn">Pending verification</Pill>;
}

/* ── Microsoft sign-in ─────────────────────────────────────────────────────────── */
function MicrosoftCard({ s, canManage, onChange }: { s: SsoSettings; canManage: boolean; onChange: (next: SsoSettings | ((prev: SsoSettings | null) => SsoSettings | null)) => void }) {
  const nav = useNav();
  const m = s.microsoft;
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<{ field: string; text: string } | null>(null);
  const locked = !canManage || !!busy;

  const patch = async (field: string, p: Parameters<typeof updateMicrosoftSignIn>[0]) => {
    setBusy(field); setErr(null);
    try { const next = await updateMicrosoftSignIn(p as { enabled?: boolean; defaultRoleKey?: string }); onChange({ ...s, microsoft: next }); }
    catch (e) { setErr({ field, text: ssoErrorText(e) }); }
    finally { setBusy(null); }
  };
  const add = async () => addDomain(domain);
  const addDomain = async (raw: string) => {
    const d = raw.trim().toLowerCase().replace(/^@/, "");
    if (!isDomain(d)) { setErr({ field: "domain", text: "Enter a domain such as example.com, without https://." }); return; }
    /* A refused domain may be added again: that is the re-check (CC 0184). */
    if (m.domains.some((x) => x.domain === d && x.status !== "refused")) { setErr({ field: "domain", text: `${d} is already listed.` }); return; }
    setBusy("domain"); setErr(null);
    try {
      const row = await addMicrosoftSignInDomain(d);
      onChange({ ...s, microsoft: { ...m, domains: [...m.domains.filter((x) => x.domain !== row.domain), row] } }); setDomain("");
      /* Only the Microsoft half is refreshed (CC follow-up 2026-10-15): replacing the whole object gave
         saml a new identity, and SamlCard re-seeds on [saml], so unsaved SAML edits were discarded. */
      setTimeout(() => { void getSsoSettings().then((n) => onChange((prev) => (prev ? { ...prev, microsoft: n.microsoft } : n)), () => {}); }, 4000);
    } catch (e) { setErr({ field: "domain", text: ssoErrorText(e) }); }
    finally { setBusy(null); }
  };
  const remove = async (d: string) => {
    setBusy("rm:" + d); setErr(null);
    try { const next = await removeMicrosoftSignInDomain(d); onChange({ ...s, microsoft: next }); }
    catch (e) { setErr({ field: "domain", text: ssoErrorText(e) }); }
    finally { setBusy(null); }
  };
  const verified = m.domains.filter((d) => d.status === "verified").length;

  const recheck = (d: string) => { setDomain(d); void addDomain(d); };
  const goMicrosoft = () => nav.go("settings.integ.detail", { id: "microsoft" });

  return (
    <Card style={{ padding: 0 }} data-testid="sso-microsoft">
      {/* One state control (CC 0222): the switch, with its own On/Off label. */}
      <Head icon={ic("lock")} title="Sign in with Microsoft"
        sub="Staff whose email is on one of these domains sign in with their Microsoft work account."
        right={<label style={{ display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0, fontSize: 12.5, fontWeight: 600, color: m.enabled ? T.n1 : T.soft }}>
          {m.enabled ? "On" : "Off"}
          <Toggle on={m.enabled} onChange={locked || (!m.enabled && verified === 0) ? undefined : () => { void patch("enabled", { enabled: !m.enabled }); }} />
        </label>} />

      {/* Who can sign in, and where the rest lives (CC 0222 §2–3): always shown. */}
      <div style={section}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }} data-testid="sso-ms-how">
          <span style={{ color: T.p1, display: "inline-flex", marginTop: 2 }}><Icon.info size={14} /></span>
          <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
            {/* Readiness (CC 0223): the sign-in page's own rule, in words. Until the server
                sends it, the general sentence stands in. */}
            {m.readiness ? (
              m.readiness.shown ? (
                <p style={{ fontSize: 12.5, color: T.n1, lineHeight: 1.55, margin: 0, display: "flex", alignItems: "center", gap: 6 }} data-testid="sso-ms-ready">
                  <span style={{ color: T.green1, display: "inline-flex" }}><Icon.check size={13} /></span>
                  Your sign-in page shows Sign in with Microsoft.
                </p>
              ) : (
                <div style={{ display: "grid", gap: 4 }} data-testid="sso-ms-blockers">
                  <p style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, margin: 0 }}>Sign in with Microsoft isn't on your sign-in page yet.</p>
                  <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 2 }}>
                    {m.readiness.blockers.map((b) => <li key={b} style={{ fontSize: 12.5, color: T.n1, lineHeight: 1.5 }}>{blockerText(b)}</li>)}
                  </ul>
                </div>
              )
            ) : (
              <p style={{ fontSize: 12.5, color: T.n1, lineHeight: 1.55, margin: 0, textWrap: "pretty" } as CSSProperties}>
                Staff sign in with Microsoft once JRNI has connected your Microsoft directory.
              </p>
            )}
            <p style={{ fontSize: 12.5, color: T.n1, lineHeight: 1.55, margin: 0 }}>Only people brought in by the Entra staff sync can sign in.</p>
            <p style={{ ...tx.help, margin: 0 }}>Microsoft app registration and staff sync are on Integrations › Microsoft.</p>
            <div><button type="button" style={linkBtn} onClick={goMicrosoft}>Staff sync settings <Icon.arrow dir="right" size={11} /></button></div>
          </div>
        </div>
        {err?.field === "enabled" ? <p style={{ ...errStyle, margin: 0 }}>{err.text}</p> : null}
        {!m.enabled && verified === 0 ? <p style={{ ...help, margin: 0 }}>Add and verify at least one domain before turning this on.</p> : null}
        {!m.directoryConnected ? (
          <div style={{ display: "flex", gap: 10, padding: "10px 12px", borderRadius: T.radius.md, background: "#FFF8E6", border: "1px solid #F2D58A" }}>
            <Icon.warn size={14} />
            <div style={{ fontSize: 12.5, color: T.n1, lineHeight: 1.5 }}>
              Domains are checked against the directory your Microsoft integration is connected to, and it isn't connected yet.
            </div>
          </div>
        ) : null}
      </div>

      <div style={section}>
        <div style={sectionTitle}>Email domains</div>
        {m.domains.length ? (
          <div style={{ border: `1px solid ${T.border}`, borderRadius: T.radius.md }}>
            {m.domains.map((d, i) => (
              <div key={d.domain} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderTop: i ? `1px solid ${T.border}` : "none", flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: T.n1, flex: "1 1 160px" }}>{d.domain}</span>
                {domainPill(d.status)}
                {canManage && d.status === "refused" ? <Btn variant="secondary" size="sm" disabled={!!busy || !m.directoryConnected} onClick={() => recheck(d.domain)}>{busy === "domain" && domain === d.domain ? "Checking…" : "Check again"}</Btn> : null}
                {canManage ? <Btn variant="ghost" size="sm" disabled={!!busy} onClick={() => { void remove(d.domain); }}>{busy === "rm:" + d.domain ? "Removing…" : "Remove"}</Btn> : null}
                {/* The reason on the row (CC 0222 §5): pending shows its reason too when the server sent one. */}
                {d.status !== "verified" ? (
                  <div style={{ flexBasis: "100%", fontSize: 11.5, color: d.status === "refused" ? T.n1 : T.soft, lineHeight: 1.5 }}>
                    {d.status === "refused" || d.reason
                      ? domainReasonText(d.reason)
                      : "We're checking that this domain belongs to your connected Microsoft directory. This updates in a few seconds."}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : <p style={{ ...help, margin: 0 }}>No domains yet.</p>}
        {canManage ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 220px", maxWidth: 320 }}>
              <Input value={domain} placeholder="example.com" disabled={!!busy || !m.directoryConnected}
                onChange={(e: { target: { value: string } }) => { setDomain(e.target.value); if (err?.field === "domain") setErr(null); }}
                onKeyDown={(e: { key: string }) => { if (e.key === "Enter") void add(); }} />
            </div>
            <Btn variant="secondary" disabled={!!busy || !domain.trim() || !m.directoryConnected} onClick={() => { void add(); }}>{busy === "domain" ? "Adding…" : "Add domain"}</Btn>
          </div>
        ) : null}
        {err?.field === "domain" ? <p style={{ ...errStyle, margin: 0 }}>{err.text}</p> : null}
      </div>

      <div style={sectionLast}>
        <label style={{ ...sectionTitle, display: "block" }} htmlFor="sso-ms-role">Role for people signing in for the first time</label>
        <RoleSelect id="sso-ms-role" roles={s.roles} value={m.defaultRoleKey} disabled={locked} onChange={(v) => { if (v) void patch("role", { defaultRoleKey: v }); }} />
        {err?.field === "role" ? <p style={{ ...errStyle, margin: 0 }}>{err.text}</p> : null}
      </div>
    </Card>
  );
}

/* ── SAML 2.0 ───────────────────────────────────────────────────────────────────── */
function certState(c: SsoSaml["certificate"]) {
  if (!c) return null;
  if (!c.expiresAt) return { tone: "ok" as const, text: "Saved. Its expiry date couldn't be read; upload it again to see it." };
  const left = Math.floor((+new Date(c.expiresAt) - Date.now()) / DAY);
  if (left < 0) return { tone: "danger" as const, text: `Expired on ${fmtDate(c.expiresAt)}. Sign-in through SAML fails until you upload a new certificate.` };
  if (left <= 30) return { tone: "warn" as const, text: `Expires in ${left} ${left === 1 ? "day" : "days"} (${fmtDate(c.expiresAt)}). Upload the new certificate from your identity provider before then.` };
  return { tone: "ok" as const, text: `Valid until ${fmtDate(c.expiresAt)}` };
}

function SamlCard({ s, canManage, onChange }: { s: SsoSettings; canManage: boolean; onChange: (next: SsoSettings) => void }) {
  const saml = s.saml;
  const sp = s.serviceProvider;
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ idpEntityId: "", loginUrl: "", certificate: "", enabled: true, defaultRoleKey: null as string | null, syncedStaffOnly: false, allowIdpInitiated: false });
  /* The server half of allowIdpInitiated (CC 0223) is live once the answer carries it, or
     carries readiness from the same packet. Until then the switch isn't drawn and the
     field isn't sent: the server's strict request would refuse an unknown key. */
  const idpInitSupported = (saml != null && typeof saml.allowIdpInitiated === "boolean") || s.microsoft.readiness !== undefined;
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<"save" | "delete" | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const metaRef = useRef<HTMLInputElement>(null);
  const [metaNote, setMetaNote] = useState<{ ok: boolean; text: string } | null>(null);
  const open = saml !== null || editing;

  useEffect(() => {
    if (saml) setF({ idpEntityId: saml.idpEntityId, loginUrl: saml.loginUrl, certificate: "", enabled: saml.enabled, defaultRoleKey: saml.defaultRoleKey, syncedStaffOnly: saml.syncedStaffOnly, allowIdpInitiated: saml.allowIdpInitiated === true });
  }, [saml]);

  const msIssuer = isMicrosoftIssuer(f.idpEntityId);
  const dirty = !saml || f.idpEntityId !== saml.idpEntityId || f.loginUrl !== saml.loginUrl || !!f.certificate || f.enabled !== saml.enabled || f.defaultRoleKey !== saml.defaultRoleKey || f.syncedStaffOnly !== saml.syncedStaffOnly || f.allowIdpInitiated !== (saml.allowIdpInitiated === true);
  const locked = !canManage || !!busy;

  const validate = () => {
    const e: Record<string, string> = {};
    if (!f.idpEntityId.trim()) e.idpEntityId = "Enter the identity provider's Entity ID (Identifier).";
    if (!/^https:\/\/\S+$/i.test(f.loginUrl.trim())) e.loginUrl = "Enter the login URL, starting with https://.";
    if (!saml?.certificate && !f.certificate.trim()) e.certificate = "Upload or paste the signing certificate.";
    if (f.certificate.trim() && !/^(-----BEGIN CERTIFICATE-----[\s\S]+-----END CERTIFICATE-----|[A-Za-z0-9+/=\s]{200,})\s*$/.test(f.certificate.trim())) e.certificate = "This doesn't look like a certificate. Paste the Base64 text or a PEM block.";
    if (f.syncedStaffOnly && !msIssuer) e.syncedStaffOnly = "Only available when the Identifier is a Microsoft one (https://sts.windows.net/<tenant id>/).";
    if (!f.defaultRoleKey) e.defaultRoleKey = "Choose a role.";
    return e;
  };
  const save = async () => {
    const e = validate(); setErrs(e); if (Object.keys(e).length) return;
    setBusy("save");
    try {
      const next = await saveSamlConnection({ idpEntityId: f.idpEntityId.trim(), loginUrl: f.loginUrl.trim(), ...(f.certificate.trim() ? { certificate: f.certificate.trim() } : {}), enabled: f.enabled, defaultRoleKey: f.defaultRoleKey ?? "", syncedStaffOnly: f.syncedStaffOnly, ...(idpInitSupported ? { allowIdpInitiated: f.allowIdpInitiated } : {}) });
      onChange({ ...s, saml: next }); setEditing(false);
      toast({ tone: "success", title: "SAML connection saved" });
    } catch (err) {
      /* 422 SSO_REQUEST_INVALID: one fields[] entry per refused field, path = body key (CC 0179). */
      const fe = (err as { fields?: { path?: string; code?: string; message?: string }[] })?.fields ?? [];
      const mapped: Record<string, string> = {};
      fe.forEach((x) => { const k = String(x.path ?? "").split(".").pop() || "_"; mapped[k] = (x.code && SSO_FIELD_TEXT[x.code]) || x.message || "This value isn't valid."; });
      setErrs(Object.keys(mapped).length ? mapped : { _: errText(err) });
    } finally { setBusy(null); }
  };
  const del = async () => {
    setBusy("delete");
    try { await deleteSamlConnection(); /* deleted: false = already gone; the end state is the same */ onChange({ ...s, saml: null }); setConfirmDel(false); setEditing(false); setF({ idpEntityId: "", loginUrl: "", certificate: "", enabled: true, defaultRoleKey: null, syncedStaffOnly: false, allowIdpInitiated: false }); toast({ tone: "success", title: "SAML connection deleted" }); }
    catch (err) { setErrs({ _: errText(err) }); }
    finally { setBusy(null); }
  };
  const onFile = async (file?: File) => {
    if (!file) return;
    const buf = new Uint8Array(await file.arrayBuffer());
    const text = new TextDecoder().decode(buf);
    const cert = /-----BEGIN CERTIFICATE-----/.test(text) ? text.trim() : btoa(String.fromCharCode(...buf));
    setF((x) => ({ ...x, certificate: cert })); setErrs((x) => ({ ...x, certificate: "" }));
  };
  /* Federation Metadata XML (CC 0222 §4): fills the three fields, which stay editable. */
  const onMetadata = async (file?: File) => {
    if (!file) return;
    const parsed = parseIdpMetadata(await file.text());
    if (!parsed || (!parsed.entityId && !parsed.loginUrl && !parsed.certificate)) {
      setMetaNote({ ok: false, text: "This file isn't identity provider metadata. Download the Federation Metadata XML from your identity provider and try again." });
      return;
    }
    setF((x) => ({
      ...x,
      ...(parsed.entityId ? { idpEntityId: parsed.entityId } : {}),
      ...(parsed.loginUrl ? { loginUrl: parsed.loginUrl } : {}),
      ...(parsed.certificate ? { certificate: parsed.certificate } : {}),
    }));
    setErrs((x) => ({ ...x, idpEntityId: "", loginUrl: "", certificate: "" }));
    const got = [parsed.entityId && "Entity ID", parsed.loginUrl && "login URL", parsed.certificate && "certificate"].filter(Boolean) as string[];
    const missing = [!parsed.entityId && "Entity ID", !parsed.loginUrl && "login URL", !parsed.certificate && "certificate"].filter(Boolean) as string[];
    setMetaNote({ ok: true, text: `Filled the ${got.join(", ")} from ${file.name}. Check them, then save.${missing.length ? ` The file had no ${missing.join(" or ")}; enter ${missing.length === 1 ? "it" : "them"} below.` : ""}` });
  };
  const cs = certState(saml?.certificate ?? null);
  const set = (k: keyof typeof f, v: unknown) => { setF((x) => ({ ...x, [k]: v })); if (errs[k]) setErrs((x) => ({ ...x, [k]: "" })); };

  return (
    <Card style={{ padding: 0 }} data-testid="sso-saml">
      <Head icon={ic("lock")} title="SAML 2.0"
        sub="Connect an identity provider such as Microsoft Entra ID, Okta or Google Workspace."
        right={saml ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {cs?.tone === "danger" ? <Pill tone="danger">Certificate expired</Pill> : cs?.tone === "warn" ? <Pill tone="warn">Certificate expiring</Pill> : null}
            {/* On only when the connection is switched on AND this deployment serves SAML (CC 0209, same rule as 0199). */}
            {saml.enabled && sp.available ? <Pill tone="success" dot>On</Pill>
              : saml.enabled ? <Pill tone="warn">Set up, not available on this deployment</Pill>
              : <Pill tone="neutral">Off</Pill>}
          </div>
        ) : <Pill tone="neutral">Not set up</Pill>} />

      {!open ? (
        <div style={{ padding: "20px 16px", display: "grid", gap: 12, justifyItems: "start" }}>
          <p style={{ fontSize: 13, color: T.n1, margin: 0, lineHeight: 1.6, maxWidth: 560 }}>
            No SAML connection yet. You'll copy a few values from this page into your identity provider, then paste its details back here.
          </p>
          {canManage ? <Btn onClick={() => setEditing(true)} data-testid="sso-saml-setup">Set up SAML</Btn> : null}
        </div>
      ) : (
        <div style={{ display: "grid" }}>
          {!sp.available ? (
            <div style={section}>
              <div role="status" style={{ display: "flex", gap: 10, padding: "10px 12px", borderRadius: T.radius.md, background: "#FFF8E6", border: "1px solid #F2D58A", fontSize: 12.5, color: T.n1, lineHeight: 1.5 }}>
                <Icon.warn size={14} />
                <span>SAML sign-in isn't switched on for this workspace's deployment yet, so the metadata link and sign-in won't work. You can still prepare the connection; ask your JRNI contact to switch it on.</span>
              </div>
            </div>
          ) : null}
          <div style={section}>
            <Step n={1} title="Give these to your identity provider" sub="Copy each value into the new SAML application, or give it our metadata file."
              right={sp.available && sp.metadataUrl ? <a href={sp.metadataUrl} download style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" }}>Download metadata</a> : undefined}>
              <div>
                <CopyRow k="Entity ID" v={sp.entityId} />
                <CopyRow k="Reply (ACS) URL" v={sp.acsUrl} />
                <CopyRow k="Sign-on URL" v={sp.signOnUrl} />
                <CopyRow k="Logout URL" v={sp.logoutUrl} />
              </div>
            </Step>
          </div>

          <div style={section}>
            <Step n={2} title="Add your identity provider's details" sub="Upload its Federation Metadata XML to fill these in, or enter them yourself."
              right={canManage ? (
                <>
                  <Btn variant="secondary" size="sm" icon={ic("upload", 13)} disabled={locked} onClick={() => metaRef.current?.click()} data-testid="sso-saml-metadata">Upload metadata XML</Btn>
                  <input ref={metaRef} type="file" accept=".xml,application/xml,text/xml" style={{ display: "none" }} onChange={(e) => { void onMetadata(e.target.files?.[0]); e.target.value = ""; }} />
                </>
              ) : undefined}>
            {metaNote ? <p role="status" style={{ ...(metaNote.ok ? help : errStyle), margin: 0 }}>{metaNote.text}</p> : null}
            <div>
              <label style={label} htmlFor="sso-idp-id">Identity provider Entity ID (Identifier)</label>
              <Input id="sso-idp-id" value={f.idpEntityId} disabled={locked} placeholder="https://sts.windows.net/00000000-0000-0000-0000-000000000000/" onChange={(e: { target: { value: string } }) => set("idpEntityId", e.target.value)} />
              {errs.idpEntityId ? <p style={errStyle}>{errs.idpEntityId}</p> : null}
            </div>
            <div>
              <label style={label} htmlFor="sso-login">Login URL</label>
              <Input id="sso-login" value={f.loginUrl} disabled={locked} placeholder="https://login.microsoftonline.com/…/saml2" onChange={(e: { target: { value: string } }) => set("loginUrl", e.target.value)} />
              {errs.loginUrl ? <p style={errStyle}>{errs.loginUrl}</p> : null}
            </div>
            <div>
              <span style={label}>Signing certificate</span>
              {saml?.certificate ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                  {cs?.tone === "ok" ? <Pill tone="success" dot>Saved</Pill> : cs?.tone === "warn" ? <Pill tone="warn">Expiring soon</Pill> : <Pill tone="danger">Expired</Pill>}
                  <span style={{ fontSize: 12.5, color: T.n1 }}>{cs?.text}</span>
                </div>
              ) : null}
              {saml?.certificate && (saml.certificate.subject || saml.certificate.thumbprint) ? (
                <div style={{ display: "grid", gap: 2, fontSize: 11.5, color: T.soft, marginBottom: 8, overflowWrap: "anywhere" }}>
                  {saml.certificate.subject ? <span>Subject: {saml.certificate.subject}</span> : null}
                  {saml.certificate.thumbprint ? <span>Thumbprint: <code style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{saml.certificate.thumbprint}</code></span> : null}
                </div>
              ) : null}
              {canManage ? (
                <>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
                    <Btn variant="secondary" size="sm" disabled={locked} onClick={() => fileRef.current?.click()}>{saml?.certificate ? "Upload a new certificate" : "Upload .cer or .pem"}</Btn>
                    <span style={{ fontSize: 11.5, color: T.soft }}>or paste the Base64 text below</span>
                    <input ref={fileRef} type="file" accept=".cer,.crt,.pem" style={{ display: "none" }} onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ""; }} />
                  </div>
                  <textarea value={f.certificate} disabled={locked} rows={4} placeholder={saml?.certificate ? "Leave empty to keep the saved certificate." : "MIIC8DCCAdigAwIBAgIQ…"}
                    onChange={(e) => set("certificate", e.target.value)}
                    style={{ width: "100%", boxSizing: "border-box", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11.5, padding: 10, borderRadius: T.radius.md, border: `1px solid ${errs.certificate ? T.red1 : T.border}`, resize: "vertical" }} />
                </>
              ) : null}
              {errs.certificate ? <p style={errStyle}>{errs.certificate}</p> : null}
            </div>
            <div>
              <label style={label} htmlFor="sso-saml-role">Role for people signing in for the first time</label>
              <RoleSelect id="sso-saml-role" roles={s.roles} value={f.defaultRoleKey} disabled={locked} onChange={(v) => set("defaultRoleKey", v)} />
              {errs.defaultRoleKey ? <p style={errStyle}>{errs.defaultRoleKey}</p> : null}
            </div>
            <div>
              <SwitchRow on={f.syncedStaffOnly} disabled={locked || (!msIssuer && !f.syncedStaffOnly)} onChange={() => set("syncedStaffOnly", !f.syncedStaffOnly)}
                title="Only staff synced from Microsoft may sign in"
                sub={msIssuer ? "Anyone the Entra staff sync did not bring in is refused." : "Available when the Identifier is a Microsoft one (https://sts.windows.net/<tenant id>/)."} />
              {errs.syncedStaffOnly ? <p style={errStyle}>{errs.syncedStaffOnly}</p> : null}
            </div>
            {idpInitSupported ? (
              <SwitchRow on={f.allowIdpInitiated} disabled={locked} onChange={() => set("allowIdpInitiated", !f.allowIdpInitiated)} testid="sso-saml-idp-initiated"
                title="Allow sign-in from Microsoft My Apps"
                sub="People can also open JRNI from their My Apps page. Leave off unless your organisation uses My Apps." />
            ) : null}
            <SwitchRow on={f.enabled} disabled={locked} onChange={() => set("enabled", !f.enabled)} title="Allow sign-in with this connection" sub="Turn off to pause SAML sign-in without losing the setup." />
            </Step>
          </div>

          {errs._ ? <div style={section}><p style={{ ...errStyle, margin: 0 }} role="alert">{errs._}</p></div> : null}

          {canManage ? (
            <div style={{ ...sectionLast, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", background: T.cardSoft }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flex: 1 }}>
                <Btn disabled={locked || !dirty} onClick={() => { void save(); }} data-testid="sso-saml-save">{busy === "save" ? "Saving…" : "Save"}</Btn>
                {!saml ? <Btn variant="secondary" disabled={locked} onClick={() => { setEditing(false); setErrs({}); }}>Cancel</Btn> : null}
                {saml ? <Btn variant="secondary" icon={ic("external", 13)} disabled={!saml.enabled || dirty || !sp.signOnUrl || !sp.available} title={!sp.available ? "SAML sign-in isn't switched on for this deployment." : dirty ? "Save first." : !saml.enabled ? "Turn the connection on first." : undefined} onClick={() => window.open(sp.signOnUrl, "_blank", "noopener")}>Test sign-in</Btn> : null}
              </div>
              {saml ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {confirmDel ? (
                    <>
                      <span style={{ fontSize: 12.5, color: T.n1 }}>Delete this connection? Staff can't sign in with SAML until it is set up again.</span>
                      <Btn variant="secondary" size="sm" disabled={!!busy} onClick={() => setConfirmDel(false)}>Keep</Btn>
                      <Btn variant="danger" size="sm" disabled={!!busy} onClick={() => { void del(); }}>{busy === "delete" ? "Deleting…" : "Delete"}</Btn>
                    </>
                  ) : <Btn variant="ghost" size="sm" disabled={!!busy} onClick={() => setConfirmDel(true)}>Delete connection</Btn>}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

/* ── Recent sign-in activity ──────────────────────────────────────────────────────── */
function ActivityCard({ rows, err, forbidden }: { rows: SsoActivityRow[] | null; err: string | null; forbidden?: boolean }) {
  const th: CSSProperties = { padding: "8px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, color: T.soft, whiteSpace: "nowrap" };
  const td: CSSProperties = { padding: "10px 16px", fontSize: 12.5, color: T.n1, borderTop: `1px solid ${T.border}`, verticalAlign: "top" };
  return (
    <Card style={{ padding: 0 }} data-testid="sso-activity">
      <Head icon={ic("eye")} title="Recent sign-in activity" sub="The last 20 single sign-on attempts." />
      {forbidden ? <p style={{ ...help, padding: "14px 16px", margin: 0 }}>Sign-in activity is visible to people who can view the audit log.</p>
        : err ? <p style={{ ...errStyle, padding: "14px 16px", margin: 0 }}>{err}</p>
        : rows === null ? <p style={{ ...help, padding: "14px 16px", margin: 0 }}>Loading…</p>
        : !rows.length ? <p style={{ ...help, padding: "14px 16px", margin: 0 }}>No single sign-on attempts yet.</p>
        : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>When</th><th style={th}>Who</th><th style={th}>Method</th><th style={th}>Result</th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...td, whiteSpace: "nowrap", color: T.soft }} title={r.at}>{fmtWhen(r.at)}</td>
                    <td style={td}><div style={{ fontWeight: 500 }}>{r.name || r.email || "No matching account"}</div>{r.name && r.email ? <div style={{ fontSize: 11.5, color: T.soft }}>{r.email}</div> : null}</td>
                    <td style={td}>{r.method === "saml" ? "SAML" : "Microsoft"}</td>
                    <td style={td}>
                      {r.result === "success" ? <Pill tone="success" dot>Signed in</Pill> : <Pill tone="danger">Refused</Pill>}
                      {r.result !== "success" && r.reason ? <div style={{ fontSize: 11.5, color: T.soft, marginTop: 4, lineHeight: 1.5 }}>{reasonText(r.reason)}</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </Card>
  );
}

/* The right-hand column (CC 0222): where each method stands, and the two related
   pages, so nothing is configured twice and the page fills the Settings width. */
function SummaryCard({ s }: { s: SsoSettings | null }) {
  const nav = useNav();
  const verified = s ? s.microsoft.domains.filter((d) => d.status === "verified").length : 0;
  const msLine = !s ? "—"
    : s.microsoft.readiness ? (s.microsoft.readiness.shown ? "On the sign-in page" : s.microsoft.enabled ? "On, not shown yet" : "Off")
    : s.microsoft.enabled ? `On · ${verified} verified ${verified === 1 ? "domain" : "domains"}` : "Off";
  const people = s?.activeDirectoryGrantCount;
  const samlLine = !s ? "—" : !s.saml ? "Not set up" : s.saml.enabled && s.serviceProvider.available ? "On" : s.saml.enabled ? "Set up, not available on this deployment" : "Off";
  const row = (k: string, v: string) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 16px", borderTop: `1px solid ${T.border}` }}>
      <span style={{ fontSize: 12.5, color: T.soft }}>{k}</span>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, textAlign: "right" }}>{v}</span>
    </div>
  );
  return (
    <div style={{ display: "grid", gap: 16, alignContent: "start", flex: "1 1 280px", minWidth: 0 }}>
      <Card style={{ padding: 0 }}>
        <div style={{ padding: "12px 16px" }}>
          <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>Sign-in methods</h3>
          <p style={{ ...tx.help, marginTop: 2 }}>What staff can use to sign in to Studio.</p>
        </div>
        {row("Microsoft", msLine)}
        {row("SAML 2.0", samlLine)}
        {/* Synced staff only (CC 0225): a SAML connection without syncedStaffOnly admits
            anyone its identity provider sends, so this is never presented as the total. */}
        {typeof people === "number" ? row("Synced staff who can sign in", s?.saml?.enabled && !s.saml.syncedStaffOnly ? `${people} · plus anyone your SAML provider admits` : String(people)) : null}
      </Card>
      <Card style={{ padding: 0 }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
          <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>Related settings</h3>
        </div>
        <div style={{ padding: "12px 16px", display: "grid", gap: 12 }}>
          <div>
            <button type="button" style={linkBtn} onClick={() => nav.go("settings.integ.detail", { id: "microsoft" })}>Integrations › Microsoft <Icon.arrow dir="right" size={11} /></button>
            <p style={{ ...tx.help, marginTop: 2 }}>The Microsoft app registration, admin consent and the Entra staff sync.</p>
          </div>
          <div>
            <button type="button" style={linkBtn} onClick={() => nav.go("settings.security")}>Security &amp; compliance <Icon.arrow dir="right" size={11} /></button>
            <p style={{ ...tx.help, marginTop: 2 }}>Password rules, sessions and the other security controls.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function SettingsSingleSignOn() {
  const nav = useNav();
  const canManage = can(SSO_MANAGE_CAP);
  const [s, setS] = useState<SsoSettings | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [rows, setRows] = useState<SsoActivityRow[] | null>(null);
  const [rowsErr, setRowsErr] = useState<string | null>(null);
  /* 403 = no audit.read (CC 0187): say so plainly, never the raw refusal. */
  const [rowsForbidden, setRowsForbidden] = useState(false);

  const load = () => {
    setLoadErr(null);
    getSsoSettings().then(setS, (e) => setLoadErr(errText(e)));
    setRowsErr(null); setRowsForbidden(false);
    listSsoSignInActivity().then(setRows, (e) => {
      if ((e as { status?: number })?.status === 403) setRowsForbidden(true);
      else setRowsErr(errText(e));
    });
  };
  useEffect(load, []);

  return (
    <SettingsShell activeTab="security">
      <PageHeader
        title="Single sign-on"
        subtitle="Let staff sign in with your organisation's Microsoft account or SAML identity provider."
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("settings.security")}><Icon.arrow dir="left" size={12} /> Security &amp; compliance</span>}
      />
      {/* Same frame as Integrations › Microsoft (CC 0222): padded scroll area, main column
          plus a side column, so the cards don't sit under the description or leave the
          right half empty. Stacks on narrow screens. */}
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px", display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start", alignContent: "flex-start" }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 16, flex: "2 1 560px", minWidth: 0 }}>
        {!canManage ? (
          <div style={{ display: "flex", gap: 10, padding: "10px 14px", borderRadius: T.radius.md, background: T.p3, color: T.n1, fontSize: 12.5, lineHeight: 1.5 }}>
            <Icon.info size={14} /> You can view these settings. Changing them needs the security settings permission.
          </div>
        ) : null}
        {loadErr ? (
          <Card style={{ padding: 16, display: "grid", gap: 10, justifyItems: "start" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>Single sign-on settings couldn't be loaded</div>
            <p style={{ ...errStyle, margin: 0 }}>{loadErr}</p>
            <Btn variant="secondary" size="sm" onClick={load}>Try again</Btn>
          </Card>
        ) : !s ? (
          <Card style={{ padding: 16 }}><p style={{ ...help, margin: 0 }}>Loading…</p></Card>
        ) : (
          <>
            {/* The grant count limits only grant-gated sign-in (CC 0225): Microsoft always, SAML
                only with syncedStaffOnly. The warning names the method(s) it is true for. */}
            {s.activeDirectoryGrantCount === 0 && (s.microsoft.enabled || (!!s.saml?.enabled && s.saml.syncedStaffOnly)) ? (
              <div role="status" data-testid="sso-zero-grants" style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 14px", borderRadius: T.radius.md, background: "#FFF8E6", border: "1px solid #F2D58A" }}>
                <Icon.warn size={14} />
                <div style={{ display: "grid", gap: 4, fontSize: 12.5, color: T.n1, lineHeight: 1.5 }}>
                  <span><strong>{s.microsoft.enabled && !!s.saml?.enabled && s.saml.syncedStaffOnly
                    ? "0 people can sign in with single sign-on yet."
                    : s.microsoft.enabled ? "0 people can sign in with Microsoft yet." : "0 people can sign in with SAML yet."}</strong> People are added by staff sync on Integrations › Microsoft 365.</span>
                  <span><button type="button" style={linkBtn} onClick={() => nav.go("settings.integ.detail", { id: "microsoft" })}>Staff sync settings <Icon.arrow dir="right" size={11} /></button></span>
                </div>
              </div>
            ) : null}
            <MicrosoftCard s={s} canManage={canManage} onChange={setS} />
            <SamlCard s={s} canManage={canManage} onChange={setS} />
          </>
        )}
        <ActivityCard rows={rows} err={rowsErr} forbidden={rowsForbidden} />
      </div>
      <SummaryCard s={s} />
      </div>
    </SettingsShell>
  );
}
